/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * The comparison a visitor last made, kept in their own browser so the Stap 1
 * card can OFFER to pick it up again (docs/plan-vergelijker-resultaten-2026-09-30.md,
 * section 12.4 B2). This module is the storage and the rules only: what is kept,
 * when, for how long, and the address that resumes it. The chip that shows the
 * offer is a component's business.
 *
 * Offered, never applied. The remembered inputs are never poured into the
 * comparer by themselves (plan sections 12.1 and 12.2), for two reasons:
 *   - at render it is a hydration mismatch: the baked HTML was rendered with the
 *     default inputs, and the bake has no storage to read;
 *   - after mount it is a layout shift, a second `results_view` for one visit,
 *     and it would overwrite a volume the visitor has already typed.
 * So a returning visitor gets one tap on a link (`resumeHref`) to the address
 * that carries the inputs, and the comparer re-derives everything from that
 * address, exactly as it does for a shared link. Nothing here writes the
 * comparer's state, and callers read in an effect after mount, never in render.
 *
 * What is kept, and when. The five inputs the comparer derives from its address
 * (audience, volume, contractDuration, smartCharging, terms) under
 * COMPARE_MEMORY_KEY as `{ v: 1, at, ...inputs }`. An entry is written only when the
 * visit's address carried a filter parameter (the caller says so) AND the inputs
 * differ from what a clean visit shows. A visit that asked for nothing leaves
 * nothing behind, so a first-time visitor's storage stays empty and "the
 * previous comparison" is always one somebody actually made. One path removes:
 * an address that carries a filter parameter AND reads as the defaults is a
 * visitor who set the filters back, so the comparison they made before is
 * undone and is no longer offered. Every other path that does not write leaves
 * storage exactly as it was.
 *
 * "The defaults" are what the comparer shows for an address without parameters
 * (comparerParams.ts answers that itself): Particulier, that audience's default
 * volume, '1 jaar', smart charging 'all', no terms filter. The audience is part
 * of that: a zakelijke visitor at the business default of 6 000 kWh is looking
 * at something a clean visit would not show, so that comparison is remembered.
 *
 * An entry is offered for 90 days after it was last written (every write
 * refreshes the stamp). One stamped more than a day ahead, from a clock that was
 * wrong when it was written, is treated as corrupt: it could never age out.
 *
 * Reading returns null for everything it cannot vouch for: absent, unparseable,
 * another version, a field the comparer could not have produced, expired, or
 * equal to the defaults. It never repairs or deletes what it rejects; the next
 * write replaces it.
 *
 * Storage basis. Remembering the filters is functional storage: it stays in the
 * visitor's own browser, is sent nowhere and carries no identifier, and the
 * cookie policy already names it (nl.json `cookies.preference_center.functional_desc`:
 * "het onthouden van je taalkeuze, filters en cookie-voorkeur"), so it needs no
 * consent and no CONSENT_VERSION bump (plan section 12.3, point 10).
 *
 * Dismissing the offer is a separate flag in sessionStorage: one dismissal
 * silences the chip for the rest of the tab (the FloatingCompareCta idiom).
 *
 * SSR-safe and private-mode-safe like articleShareMemory.ts: every storage
 * access sits behind a `typeof window` guard and a try/catch (blocked site data
 * makes the accessor itself throw), and a failure reads as "nothing remembered".
 * Pure: no React, no router, no i18n. `now` is a parameter, so tests never
 * depend on the clock.
 */

import { AUDIENCE_VALUES, readComparerInputs } from './comparerParams';
import { defaultAnnualKwh } from './earnings';

/** localStorage: the last comparison, `{ v: 1, at, ...inputs }`. */
export const COMPARE_MEMORY_KEY = 'ere:compare:last';
/** Days an entry is offered after it was last written. */
export const COMPARE_MEMORY_TTL_DAYS = 90;
/** sessionStorage: '1' once the visitor dismissed the offer in this tab. */
export const RESUME_DISMISSED_KEY = 'ere:compare:resume_dismissed';

const DAY_MS = 24 * 60 * 60 * 1000;
const TTL_MS = COMPARE_MEMORY_TTL_DAYS * DAY_MS;
/** A stamp further ahead than this is a clock that was wrong, not a recent visit. */
const FUTURE_TOLERANCE_MS = DAY_MS;
/** `contractDuration` and `terms` are data identifiers ('1 jaar', 'no_exit_fee'), never prose. */
const MAX_IDENTIFIER_LENGTH = 40;

const SMART_CHARGING_VALUES = ['all', 'has', 'none'] as const;
type SmartCharging = (typeof SMART_CHARGING_VALUES)[number];

/** The five inputs the comparer derives from its address, with the comparer's own meanings. */
export interface CompareInputs {
  /** One of AUDIENCE_VALUES. A plain string so a caller needs no cast. */
  audience: string;
  volume: number;
  contractDuration: string;
  smartCharging: SmartCharging;
  /**
   * A FEATURE_CODES value (src/lib/providerFeatures.ts), or null for "no terms
   * filter". The engine's own token for that, 'all', means the same and is
   * stored as null.
   */
  terms: string | null;
}

export interface CompareMemory extends CompareInputs {
  v: 1;
  /** Epoch milliseconds of the last write. */
  at: number;
}

/**
 * What the comparer shows for an address without a single parameter. Asked of
 * the reader itself, so this cannot drift from comparerParams.ts. Smart
 * charging and terms have no shared module: the engine's own fallback for both
 * is 'all' (ComparisonEngine.tsx), kept here as 'all' and as no terms.
 */
const CLEAN_VISIT = readComparerInputs(new URLSearchParams());
const DEFAULT_SMART_CHARGING: SmartCharging = 'all';

/**
 * "No terms filter" is null here, and also the engine's own token for it, 'all'
 * (what `terms=all` carries in an address). A caller that passes its filter
 * value on as it is therefore cannot turn the defaults into a remembered
 * comparison.
 */
const NO_TERMS_TOKEN = 'all';
const isNoTerms = (terms: unknown): terms is null | typeof NO_TERMS_TOKEN =>
  terms === null || terms === NO_TERMS_TOKEN;

/**
 * True when `inputs` are exactly what a clean visit shows, so resuming them
 * would change nothing. The volume default follows the audience
 * (`defaultAnnualKwh`); the audience itself must be the default one.
 */
export function isDefaultInputs(inputs: CompareInputs): boolean {
  return (
    inputs.audience === CLEAN_VISIT.audience &&
    inputs.volume === defaultAnnualKwh(inputs.audience) &&
    inputs.contractDuration === CLEAN_VISIT.contractDuration &&
    inputs.smartCharging === DEFAULT_SMART_CHARGING &&
    isNoTerms(inputs.terms)
  );
}

const isAudience = (value: unknown): value is string =>
  typeof value === 'string' && (AUDIENCE_VALUES as readonly string[]).includes(value);

const isSmartCharging = (value: unknown): value is SmartCharging =>
  typeof value === 'string' && (SMART_CHARGING_VALUES as readonly string[]).includes(value);

const isIdentifier = (value: unknown): value is string =>
  typeof value === 'string' && value.length > 0 && value.length <= MAX_IDENTIFIER_LENGTH;

/** A candidate's `terms`: null for "no filter", a code, or undefined when it is neither. */
function readTerms(value: unknown): string | null | undefined {
  if (isNoTerms(value)) return null;
  return isIdentifier(value) ? value : undefined;
}

/**
 * The five inputs of `candidate` when every one of them is a value the comparer
 * could have produced, else null. Shared by both directions, so storage never
 * holds an entry the reader would refuse, and it never carries a stranger's key
 * along. A volume is a whole number of kWh: the comparer only ever derives it
 * with `parseInt`, and an exponent or a fraction would not survive the address.
 */
function validInputs(candidate: unknown): CompareInputs | null {
  if (typeof candidate !== 'object' || candidate === null) return null;
  const { audience, volume, contractDuration, smartCharging, terms } = candidate as Record<
    string,
    unknown
  >;
  if (!isAudience(audience)) return null;
  if (typeof volume !== 'number' || !Number.isSafeInteger(volume) || volume <= 0) return null;
  if (!isIdentifier(contractDuration)) return null;
  if (!isSmartCharging(smartCharging)) return null;
  const termsOrNone = readTerms(terms);
  if (termsOrNone === undefined) return null;
  return { audience, volume, contractDuration, smartCharging, terms: termsOrNone };
}

/**
 * Remember the comparison the comparer is showing. Writes only when the visit's
 * address carried a filter parameter AND the inputs are not the defaults. With
 * a filter parameter and the defaults, the earlier entry is removed: the
 * visitor set the filters back, and an offer to resume the comparison they
 * undid would be wrong. A plain visit and inputs the reader would refuse leave
 * storage untouched. Never throws: a blocked or full store just means nothing
 * is remembered.
 */
export function rememberCompareInputs(
  inputs: CompareInputs,
  opts: { urlHadFilterParam: boolean; now?: number }
): void {
  if (typeof window === 'undefined') return;
  try {
    if (!opts.urlHadFilterParam) return;
    const valid = validInputs(inputs);
    if (!valid) return;
    if (isDefaultInputs(valid)) {
      window.localStorage.removeItem(COMPARE_MEMORY_KEY);
      return;
    }
    const entry: CompareMemory = { v: 1, at: opts.now ?? Date.now(), ...valid };
    window.localStorage.setItem(COMPARE_MEMORY_KEY, JSON.stringify(entry));
  } catch {
    /* private mode / blocked site data / quota: nothing is remembered, nothing breaks */
  }
}

/**
 * The remembered comparison, or null when there is nothing worth offering:
 * absent, corrupt, another version, a field outside what the comparer can
 * produce, older than COMPARE_MEMORY_TTL_DAYS, or equal to the defaults. An
 * entry is still offered on its 90th day. Never throws, never writes.
 */
export function readCompareMemory(now: number = Date.now()): CompareMemory | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(COMPARE_MEMORY_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return null;
    const { v, at } = parsed as Record<string, unknown>;
    if (v !== 1) return null;
    if (typeof at !== 'number' || !Number.isFinite(at)) return null;
    const age = now - at;
    if (age > TTL_MS || age < -FUTURE_TOLERANCE_MS) return null;
    const inputs = validInputs(parsed);
    if (!inputs || isDefaultInputs(inputs)) return null;
    return { v: 1, at, ...inputs };
  } catch {
    return null;
  }
}

/** Forget the remembered comparison. Leaves every other key alone. Never throws. */
export function clearCompareMemory(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(COMPARE_MEMORY_KEY);
  } catch {
    /* storage unavailable: nothing to forget */
  }
}

/** True once the visitor dismissed the offer in this tab. False when sessionStorage is unavailable. */
export function isResumeDismissed(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.sessionStorage.getItem(RESUME_DISMISSED_KEY) === '1';
  } catch {
    return false; // storage blocked: behave as never dismissed
  }
}

/** Silence the offer for the rest of this tab. A no-op when sessionStorage is unavailable. */
export function dismissResumeForSession(): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(RESUME_DISMISSED_KEY, '1');
  } catch {
    /* storage blocked: the caller's own state still hides the chip for this page view */
  }
}

/**
 * The address that resumes a remembered comparison, for example
 * `/nl?audience=Zakelijk&volume=4000&contractDuration=2+jaar#vergelijken`.
 * `anchor` is the results block's id for the language (`getCompareAnchor`).
 *
 * Only what differs from the defaults is written; the comparer re-derives the
 * rest, so Zakelijk at its own 6 000 kWh is just `?audience=Zakelijk`, while
 * any other figure on either audience is a typed volume and
 * stays. The order is fixed: audience, volume, contractDuration, smartCharging,
 * terms. It is written with URLSearchParams like the comparer's own controls
 * (the router serialises a URLSearchParams), so a space is `+` and the reader
 * (`readComparerInputs`) takes it back. '1 jaar' is the default looptijd
 * (since 2026-10-30; 'Alle' was, from 2026-10-16) and is left out like every
 * default; 'Alle', another looptijd or a comma list of them (the multi-select)
 * is kept. With nothing to say there is no `?`.
 */
export function resumeHref(memory: CompareInputs, language: string, anchor: string): string {
  const params = new URLSearchParams();
  if (memory.audience !== CLEAN_VISIT.audience) params.set('audience', memory.audience);
  if (memory.volume !== defaultAnnualKwh(memory.audience)) {
    params.set('volume', String(memory.volume));
  }
  if (memory.contractDuration !== CLEAN_VISIT.contractDuration) {
    params.set('contractDuration', memory.contractDuration);
  }
  if (memory.smartCharging !== DEFAULT_SMART_CHARGING) {
    params.set('smartCharging', memory.smartCharging);
  }
  if (!isNoTerms(memory.terms)) params.set('terms', memory.terms);
  const query = params.toString();
  return `/${language}${query ? `?${query}` : ''}${anchor ? `#${anchor}` : ''}`;
}
